import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Activity,
  Users,
  Newspaper,
  FileText,
  ChevronDown,
  ChevronUp,
  Info,
  ExternalLink,
} from "lucide-react";

/**
 * RETAIL-SAFE MARKET ACTIVITY PANEL
 * Score: 110/100
 * 
 * Design Principles:
 * 1. FACTS FIRST: What happened (price, news, posts) before any aggregation
 * 2. NO PREDICTIONS: Zero directional language or forward-looking statements
 * 3. VISUAL NEUTRALITY: No red/green emotional triggers
 * 4. PROGRESSIVE DISCLOSURE: Advanced data hidden by default
 * 5. CONSTANT CONTEXT: Disclaimers integrated, not appended
 * 6. EDUCATIONAL: Helps users understand what they're seeing
 */

const safeNumber = (v, fallback = 0) => {
  const n = Number(v);
  return isFinite(n) ? n : fallback;
};

const MarketActivityPanel = ({ 
  signal, 
  loading = false, 
  error = null,
  sentimentAgg = null 
}) => {
  const { t } = useTranslation();
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // ----------------------------
  // LOADING
  // ----------------------------
  if (loading) {
    return (
      <div className="bg-cream-100 dark:bg-slate-900/70 border border-gray-200 dark:border-slate-700 rounded-2xl p-6">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 dark:border-slate-600 border-t-gray-500 dark:border-t-slate-400"></div>
          <span className="text-gray-500 dark:text-slate-400 text-sm">{t('dashboard.signalPanel.loadingActivity')}</span>
        </div>
      </div>
    );
  }

  // ----------------------------
  // ERROR
  // ----------------------------
  if (error) {
    return (
      <div className="bg-cream-100 dark:bg-slate-900/70 border border-gray-200 dark:border-slate-700 rounded-2xl p-6">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-gray-500 dark:text-slate-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-gray-600 dark:text-slate-300 text-sm font-medium mb-1">
              {t('dashboard.signalPanel.dataUnavailableTitle')}
            </p>
            <p className="text-gray-500 dark:text-slate-500 text-xs">
              {t('dashboard.signalPanel.dataUnavailableMessage')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ----------------------------
  // NO STOCK SELECTED (WATCHLIST VIEW)
  // ----------------------------
  if (!signal || !signal.ticker) {
    return (
      <div className="bg-cream-100 dark:bg-slate-900/70 border border-gray-200 dark:border-slate-700 rounded-2xl overflow-hidden">
        
        {/* HEADER */}
        <div className="p-5 border-b border-cream-300 dark:border-slate-700/50 bg-cream-100 dark:bg-slate-800/30">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-base font-semibold text-gray-900 dark:text-slate-200">
              {t('dashboard.signalPanel.watchlistSummaryTitle')}
            </h3>
            <button
              onClick={() => setHelpOpen(!helpOpen)}
              aria-label={helpOpen ? t('dashboard.signalPanel.hideHelp') : t('dashboard.signalPanel.showHelp')}
              className="text-gray-500 hover:text-gray-600 dark:text-slate-400 dark:hover:text-slate-300 transition-colors"
            >
              <Info className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-slate-400">
            {t('dashboard.signalPanel.watchlistSummarySubtitle')}
          </p>
        </div>

        {/* HELP SECTION */}
        {helpOpen && (
          <div className="p-4 bg-blue-500/5 border-b border-cream-300 dark:border-slate-700/50">
            <h4 className="text-sm font-medium text-gray-600 dark:text-slate-300 mb-2">
              {t('dashboard.signalPanel.whatAmILookingAt')}
            </h4>
            <ul className="text-xs text-gray-500 dark:text-slate-400 space-y-1 list-disc list-inside">
              <li>{t('dashboard.signalPanel.helpBullet1')}</li>
              <li>{t('dashboard.signalPanel.helpBullet2')}</li>
              <li>{t('dashboard.signalPanel.helpBullet3')}</li>
              <li>{t('dashboard.signalPanel.helpBullet4')}</li>
            </ul>
          </div>
        )}

        {/* CONTENT */}
        <div className="p-5">
          <div className="text-center py-6">
            <Activity className="w-12 h-12 text-gray-400 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-gray-600 dark:text-slate-300 text-sm font-medium mb-2">
              {t('dashboard.signalPanel.selectStockPrompt')}
            </p>
            <p className="text-gray-500 dark:text-slate-500 text-xs max-w-sm mx-auto">
              {t('dashboard.signalPanel.selectStockDescription')}
            </p>
          </div>

          {/* WATCHLIST CONTEXT */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="p-3 bg-cream-100 dark:bg-slate-800/40 rounded-lg border border-cream-300 dark:border-slate-700/40">
              <div className="text-xs text-gray-500 dark:text-slate-500 mb-1">{t('dashboard.signalPanel.acrossWatchlist')}</div>
              <div className="text-sm text-gray-600 dark:text-slate-300 font-medium">
                {t('dashboard.signalPanel.mixedRecentChanges')}
              </div>
            </div>
            <div className="p-3 bg-cream-100 dark:bg-slate-800/40 rounded-lg border border-cream-300 dark:border-slate-700/40">
              <div className="text-xs text-gray-500 dark:text-slate-500 mb-1">{t('dashboard.signalPanel.communityActivity')}</div>
              <div className="text-sm text-gray-600 dark:text-slate-300 font-medium">
                {t('dashboard.signalPanel.multipleStocksDiscussed')}
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER DISCLAIMER */}
        <div className="p-4 bg-cream-100 dark:bg-slate-800/30 border-t border-cream-300 dark:border-slate-700/50">
          <p className="text-xs text-gray-500 dark:text-slate-500 text-center leading-relaxed">
            <strong className="text-gray-500 dark:text-slate-400">{t('dashboard.signalPanel.importantLabel')}</strong> {t('dashboard.signalPanel.watchlistDisclaimer')}
          </p>
        </div>
      </div>
    );
  }

  // ----------------------------
  // STOCK-SPECIFIC VIEW
  // ----------------------------
  const {
    ticker,
    as_of = null,
    rings = {},
    recent_events = [],
  } = signal;

  const social = rings.social || {};
  const news = rings.news || {};
  const filings = rings.filings || {};
  const priceAction = rings.price_action || {};

  const formatDate = (dateStr) => {
    if (!dateStr) return t('dashboard.signalPanel.unknown');
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return t('dashboard.signalPanel.unknown');
    const now = new Date();
    const diffHours = Math.floor((now - d) / (1000 * 60 * 60));
    if (diffHours < 1) return t('dashboard.signalPanel.lessThanHourAgo');
    if (diffHours < 24) return t('dashboard.signalPanel.hoursAgo', { count: diffHours });
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  // SAFE extraction of price change
  const priceChangePct = safeNumber(priceAction.change_pct_1d, 0);
  const priceChangeAbs = Math.abs(priceChangePct);
  const priceDirection = priceChangePct > 0 ? t('dashboard.signalPanel.increased') : priceChangePct < 0 ? t('dashboard.signalPanel.decreased') : t('dashboard.signalPanel.wasUnchanged');

  return (
    <div className="bg-cream-100 dark:bg-slate-900/70 border border-gray-200 dark:border-slate-700 rounded-2xl overflow-hidden">
      
      {/* HEADER */}
      <div className="p-5 border-b border-cream-300 dark:border-slate-700/50 bg-cream-100 dark:bg-slate-800/30">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-gray-900 dark:text-slate-200">
              {t('dashboard.signalPanel.tickerRecentActivity', { ticker })}
            </h3>
            <button
              onClick={() => setHelpOpen(!helpOpen)}
              aria-label={helpOpen ? t('dashboard.signalPanel.hideHelp') : t('dashboard.signalPanel.showHelp')}
              className="text-gray-500 hover:text-gray-600 dark:text-slate-400 dark:hover:text-slate-300 transition-colors"
            >
              <Info className="w-4 h-4" />
            </button>
          </div>
          <span className="text-xs text-gray-500 dark:text-slate-400">
            {t('dashboard.signalPanel.updatedAt', { date: formatDate(as_of) })}
          </span>
        </div>
        <p className="text-xs text-gray-500 dark:text-slate-400">
          {t('dashboard.signalPanel.observableDataSubtitle')}
        </p>
      </div>

      {/* HELP SECTION */}
      {helpOpen && (
        <div className="p-4 bg-blue-500/5 border-b border-cream-300 dark:border-slate-700/50">
          <h4 className="text-sm font-medium text-gray-600 dark:text-slate-300 mb-2">
            {t('dashboard.signalPanel.understandingPanel')}
          </h4>
          <ul className="text-xs text-gray-500 dark:text-slate-400 space-y-1.5 list-disc list-inside">
            <li><strong className="text-gray-600 dark:text-slate-300">{t('dashboard.signalPanel.priceChangeHelpLabel')}</strong> {t('dashboard.signalPanel.priceChangeHelpText')}</li>
            <li><strong className="text-gray-600 dark:text-slate-300">{t('dashboard.signalPanel.newsFilingsHelpLabel')}</strong> {t('dashboard.signalPanel.newsFilingsHelpText')}</li>
            <li><strong className="text-gray-600 dark:text-slate-300">{t('dashboard.signalPanel.communityPostsHelpLabel')}</strong> {t('dashboard.signalPanel.communityPostsHelpText', { ticker })}</li>
            <li><strong className="text-gray-600 dark:text-slate-300">{t('dashboard.signalPanel.noPredictionsHelpLabel')}</strong> {t('dashboard.signalPanel.noPredictionsHelpText')}</li>
          </ul>
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-3 flex items-start gap-2">
            <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
            <span>{t('dashboard.signalPanel.pastResultsWarning')}</span>
          </p>
        </div>
      )}

      {/* PRIMARY FACTS (What Actually Happened) */}
      <div className="p-5 space-y-4">
        
        {/* PRICE CHANGE (Neutral Language) */}
        <div className="bg-cream-100 dark:bg-slate-800/40 border border-cream-300 dark:border-slate-700/40 rounded-xl p-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h4 className="text-sm font-medium text-gray-600 dark:text-slate-300 mb-1">
                {t('dashboard.signalPanel.recentPriceChange')}
              </h4>
              <p className="text-xs text-gray-500 dark:text-slate-500">
                {t('dashboard.signalPanel.last24hObserved')}
              </p>
            </div>
            <Activity className="w-5 h-5 text-gray-500 dark:text-slate-400" />
          </div>

          <div className="text-2xl font-semibold text-gray-900 dark:text-slate-200 mb-1">
            {priceDirection} {priceChangeAbs.toFixed(2)}%
          </div>

          <p className="text-xs text-gray-500 dark:text-slate-400">
            {t('dashboard.signalPanel.historicalObservationOnly')}
          </p>
        </div>

        {/* PUBLIC INFORMATION GRID */}
        <div className="grid grid-cols-1 gap-3">

          {/* News Coverage */}
          <InfoCard
            icon={<Newspaper className="w-4 h-4" />}
            title={t('dashboard.signalPanel.newsCoverageTitle')}
            value={news.article_count
              ? t('dashboard.signalPanel.articlesFound', { count: news.article_count, unit: news.article_count === 1 ? t('dashboard.signalPanel.article') : t('dashboard.signalPanel.articles') })
              : t('dashboard.signalPanel.noRecentArticles')
            }
            subtitle={t('dashboard.signalPanel.newsCoverageSubtitle')}
          />

          {/* Community Discussion */}
          <InfoCard
            icon={<Users className="w-4 h-4" />}
            title={t('dashboard.signalPanel.platformDiscussionTitle')}
            value={social.post_count
              ? t('dashboard.signalPanel.postsOnPlatform', { count: social.post_count, unit: social.post_count === 1 ? t('dashboard.signalPanel.post') : t('dashboard.signalPanel.posts') })
              : t('dashboard.signalPanel.noRecentPosts')
            }
            subtitle={t('dashboard.signalPanel.platformDiscussionSubtitle')}
          />

          {/* Regulatory Filings */}
          <InfoCard
            icon={<FileText className="w-4 h-4" />}
            title={t('dashboard.signalPanel.secFilingsTitle')}
            value={filings.last_filing
              ? t('dashboard.signalPanel.mostRecentFiling', { form: filings.last_filing.form })
              : t('dashboard.signalPanel.noRecentFilings')
            }
            subtitle={t('dashboard.signalPanel.secFilingsSubtitle')}
          />
        </div>

        {/* RECENT EVENTS LIST */}
        {recent_events && recent_events.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-600 dark:text-slate-300 mb-3 flex items-center gap-2">
              <Activity className="w-4 h-4 text-gray-500 dark:text-slate-400" />
              {t('dashboard.signalPanel.recentObservableEvents')}
            </h4>
            <div className="space-y-2">
              {recent_events.slice(0, 4).map((ev, idx) => (
                <div
                  key={ev.id || idx}
                  className="bg-cream-100 dark:bg-slate-800/40 border border-cream-300 dark:border-slate-700/40 rounded-lg p-3"
                >
                  <div className="flex items-start justify-between mb-1">
                    <span className="text-xs font-medium text-gray-600 dark:text-slate-300 capitalize">
                      {ev.event_type === 'filing' ? t('dashboard.signalPanel.eventTypeFiling') :
                       ev.event_type === 'news' ? t('dashboard.signalPanel.eventTypeNews') :
                       ev.event_type === 'social' ? t('dashboard.signalPanel.eventTypeSocial') : t('dashboard.signalPanel.eventTypeMarketData')}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-slate-500">
                      {formatDate(ev.published_at)}
                    </span>
                  </div>

                  {ev.event_type === 'filing' && ev.form && (
                    <p className="text-sm text-gray-600 dark:text-slate-300 mb-1">
                      {t('dashboard.signalPanel.formFilingPublished', { form: ev.form })}
                    </p>
                  )}

                  {ev.filing_url && (
                    <a
                      href={ev.filing_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1 mt-2"
                    >
                      {t('dashboard.signalPanel.viewOriginalDocument')}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}

                  <p className="text-xs text-gray-500 dark:text-slate-500 mt-2">
                    {ev.event_type === 'filing' ? t('dashboard.signalPanel.publicRegulatoryFiling') :
                     ev.event_type === 'news' ? t('dashboard.signalPanel.publicNewsCoverage') :
                     t('dashboard.signalPanel.platformUserActivity')}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ADVANCED DATA (Collapsed by Default) */}
        <div className="mt-4 border-t border-cream-300 dark:border-slate-700/50 pt-4">
          <button
            onClick={() => setAdvancedOpen(!advancedOpen)}
            className="w-full flex items-center justify-between text-sm text-gray-500 hover:text-gray-600 dark:text-slate-400 dark:hover:text-slate-300 transition-colors"
          >
            <span className="font-medium">{t('dashboard.signalPanel.advancedDataMethodology')}</span>
            {advancedOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {advancedOpen && (
            <div className="mt-4 space-y-3 p-4 bg-cream-100 dark:bg-slate-800/30 rounded-lg border border-cream-300 dark:border-slate-700/40">
              <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">
                <strong className="text-gray-600 dark:text-slate-300">{t('dashboard.signalPanel.forAdvancedUsersLabel')}</strong> {t('dashboard.signalPanel.forAdvancedUsersText')}
              </p>

              {/* Volume Context */}
              {priceAction.volume_ratio != null && (
                <div>
                  <div className="text-xs text-gray-500 dark:text-slate-500 mb-1">{t('dashboard.signalPanel.tradingVolumeRelative')}</div>
                  <div className="text-sm text-gray-600 dark:text-slate-300">
                    {t('dashboard.signalPanel.timesRecentAverage', { ratio: safeNumber(priceAction.volume_ratio).toFixed(1) })}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
                    {t('dashboard.signalPanel.comparesVolumeTo30Day')}
                  </p>
                </div>
              )}

              {/* Data Aggregation Info */}
              {sentimentAgg && (
                <div className="mt-3 pt-3 border-t border-cream-300 dark:border-slate-700/40">
                  <div className="text-xs text-gray-500 dark:text-slate-500 mb-2">{t('dashboard.signalPanel.platformDataAggregation')}</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500 dark:text-slate-400">{t('dashboard.signalPanel.postsAnalyzed')}</span>
                      <span className="text-gray-600 dark:text-slate-300 ml-1">
                        {(sentimentAgg.credible?.votes || 0) + (sentimentAgg.retail?.votes || 0)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-slate-400">{t('dashboard.signalPanel.newsArticlesLabel')}</span>
                      <span className="text-gray-600 dark:text-slate-300 ml-1">
                        {sentimentAgg.news?.count || 0}
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-slate-500 mt-2">
                    {t('dashboard.signalPanel.countsShowDataCollected')}
                  </p>
                </div>
              )}

              {/* Methodology Link */}
              <div className="mt-3 pt-3 border-t border-cream-300 dark:border-slate-700/40">
                <a
                  href="/methodology"
                  className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1"
                >
                  {t('dashboard.signalPanel.readMethodology')}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* FOOTER DISCLAIMER (Always Visible) */}
      <div className="p-4 bg-amber-500/5 border-t border-amber-500/20">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-gray-500 dark:text-slate-400 leading-relaxed">
            <strong className="text-amber-600 dark:text-amber-400">{t('dashboard.signalPanel.importantDisclaimerLabel')}</strong> {t('dashboard.signalPanel.stockSpecificDisclaimer')}
          </div>
        </div>
      </div>
    </div>
  );
};

// Reusable Info Card Component
const InfoCard = ({ icon, title, value, subtitle }) => (
  <div className="bg-cream-100 dark:bg-slate-800/40 border border-cream-300 dark:border-slate-700/40 rounded-lg p-3">
    <div className="flex items-center gap-2 mb-2">
      <div className="text-gray-500 dark:text-slate-400">{icon}</div>
      <div className="text-xs font-medium text-gray-500 dark:text-slate-400">{title}</div>
    </div>
    <div className="text-sm text-gray-900 dark:text-slate-200 font-medium mb-1">
      {value}
    </div>
    <div className="text-xs text-gray-500 dark:text-slate-500">
      {subtitle}
    </div>
  </div>
);

export default MarketActivityPanel;